import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "@emotion/styled";
import Text from "@components/Text";
import Button from "@components/Button";
import Spinner from "@components/Spinner";
import Switch from "@components/Switch";
import NumericInput from "@components/NumericInput";
import {
  getSettingsSchema,
  getUserSettings,
  updateUserSettings,
} from "@services/adminService";
import { MOBILE_BREAKPOINT } from "@src/hooks/useIsMobile";

/**
 * Per-user settings editor.
 *
 * The settings document is free-form JSON validated against a JSON Schema that
 * lives in the DB (Admin → Settings schema), so this form is built at runtime:
 * every schema property becomes a control chosen from its `type`/`enum`, and
 * keys that are stored on the user but missing from the schema still get a row
 * so nothing silently disappears. KNOWN_FIELDS below adds human labels, option
 * wording and product defaults for the keys the backend actually reads — the
 * schema row alone carries neither.
 *
 * "Edit as JSON" stays available for object/array values and for adding keys
 * the schema does not describe yet.
 */

// ── Setting keys the backend reads ─────────────────────────────
// fieldService.getUserHexSettings accepts either spelling of each key and
// prefers the snake_case one, so the form edits whichever spelling is already
// in use and falls back to snake_case for a fresh document.
const HEX_KEYS = ["hex_resolution", "hexResolution"];
const AREA_KEYS = ["areaThresh", "areaThreshold"];

/** Product default for the hex grid (backend DEFAULT_HEX_RESOLUTION). */
const DEFAULT_HEX_RESOLUTION = 13;
/** Product default for the mixed-mode area cut-off, in hectares. */
const DEFAULT_AREA_THRESHOLD = 50;

type FieldKind = "select" | "boolean" | "number" | "text" | "json";

interface FieldOption {
  value: unknown;
  label: string;
}

interface KnownField {
  label: string;
  description?: string;
  kind: FieldKind;
  options?: FieldOption[];
  unit?: string;
  min?: number;
  max?: number;
  /**
   * Fallback for a key the schema does not give a `default` for. The schema row
   * wins when it has one — it is the value the backend itself resolves for a
   * user with nothing stored, so the form must not show something else.
   */
  default?: unknown;
  /** Hide the row unless this returns true (e.g. a dependent threshold). */
  visibleWhen?: (values: Record<string, unknown>) => boolean;
}

const hexValue = (values: Record<string, unknown>): number | undefined => {
  for (const k of HEX_KEYS) {
    if (typeof values[k] === "number") return values[k] as number;
  }
  return undefined;
};

const KNOWN_FIELDS: Record<string, KnownField> = {
  hex_resolution: {
    label: "Hex resolution",
    description:
      "H3 grid used for yield maps, productivity and prescriptions. Finer grids show more detail but need more points per cell.",
    kind: "select",
    default: DEFAULT_HEX_RESOLUTION,
    options: [
      { value: 13, label: "13 — ~44 m² cells" },
      { value: 14, label: "14 — ~6 m² cells (finest)" },
      {
        value: 12,
        label: "12 — ~310 m² cells, mixed: fields under the area threshold still use 13",
      },
    ],
  },
  areaThresh: {
    label: "Area threshold",
    description:
      "Only used with resolution 12: fields at or below this area are mapped at resolution 13 instead.",
    kind: "number",
    unit: "ha",
    min: 0,
    default: DEFAULT_AREA_THRESHOLD,
    visibleWhen: (values) => hexValue(values) === 12,
  },
};
// Same metadata under the camelCase spellings.
KNOWN_FIELDS.hexResolution = KNOWN_FIELDS.hex_resolution;
KNOWN_FIELDS.areaThreshold = KNOWN_FIELDS.areaThresh;

interface SchemaProp {
  type?: string;
  title?: string;
  description?: string;
  enum?: unknown[];
  default?: unknown;
  minimum?: number;
  maximum?: number;
}

interface Field extends KnownField {
  key: string;
}

/** "hex_resolution" → "Hex resolution", "areaThresh" → "Area thresh". */
function humanizeKey(key: string): string {
  const spaced = key
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}

function kindFromType(type?: string): FieldKind | undefined {
  if (type === "boolean") return "boolean";
  if (type === "number" || type === "integer") return "number";
  if (type === "string") return "text";
  if (type === "object" || type === "array") return "json";
  return undefined;
}

function kindFromValue(value: unknown): FieldKind {
  if (typeof value === "boolean") return "boolean";
  if (typeof value === "number") return "number";
  if (typeof value === "string") return "text";
  return "json";
}

/**
 * Merge the runtime schema with KNOWN_FIELDS into the rows to render. Schema
 * order first (that is the order an admin authored), then any stored key the
 * schema does not mention, then known keys that neither side has yet.
 */
function buildFields(
  schemaProps: Record<string, SchemaProp>,
  values: Record<string, unknown>
): Field[] {
  const keys: string[] = [];
  const push = (k: string) => {
    if (!keys.includes(k)) keys.push(k);
  };
  Object.keys(schemaProps).forEach(push);
  Object.keys(values).forEach(push);
  // A fresh document still gets the keys the backend reads.
  if (!HEX_KEYS.some((k) => keys.includes(k))) push(HEX_KEYS[0]);
  if (!AREA_KEYS.some((k) => keys.includes(k))) push(AREA_KEYS[0]);

  return keys.map((key) => {
    const known = KNOWN_FIELDS[key];
    const prop = schemaProps[key] ?? {};
    const options: FieldOption[] | undefined =
      known?.options ??
      (Array.isArray(prop.enum)
        ? prop.enum.map((v) => ({ value: v, label: String(v) }))
        : undefined);
    const kind: FieldKind =
      known?.kind ??
      (options ? "select" : kindFromType(prop.type) ?? kindFromValue(values[key]));

    return {
      key,
      label: known?.label ?? prop.title ?? humanizeKey(key),
      description: known?.description ?? prop.description,
      kind,
      options,
      unit: known?.unit,
      min: known?.min ?? prop.minimum,
      max: known?.max ?? prop.maximum,
      default: prop.default ?? known?.default,
      visibleWhen: known?.visibleWhen,
    };
  });
}

/** Fill in missing values so every rendered control has something to show. */
function withDefaults(
  values: Record<string, unknown>,
  fields: Field[]
): Record<string, unknown> {
  const out = { ...values };
  for (const f of fields) {
    if (out[f.key] !== undefined) continue;
    if (f.default !== undefined) out[f.key] = f.default;
  }
  return out;
}

/** Key-order-independent comparison, so a re-serialised document is not "dirty". */
function stableStringify(value: unknown): string {
  if (value === null || typeof value !== "object") return JSON.stringify(value) ?? "null";
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(",")}]`;
  const obj = value as Record<string, unknown>;
  return `{${Object.keys(obj)
    .sort()
    .map((k) => `${JSON.stringify(k)}:${stableStringify(obj[k])}`)
    .join(",")}}`;
}

// ── Styled ─────────────────────────────────────────────────────

const Container = styled.div`
  max-width: 900px;
  width: 100%;
`;

const EditorHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
  flex-wrap: wrap;
`;

const ModeButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-family: Nunito Sans, sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #68ad18;
  &:hover {
    text-decoration: underline;
  }
`;

const Card = styled.div`
  background: #fafafa;
  border: 1px solid #ebeeef;
  border-radius: 8px;
  padding: 8px 24px;
`;

const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 280px minmax(0, 1fr);
  gap: 16px;
  align-items: center;
  padding: 16px 0;
  border-bottom: 1px solid #ebeeef;
  &:last-child {
    border-bottom: none;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    grid-template-columns: 1fr;
    gap: 8px;
    align-items: flex-start;
  }
`;

const FieldLabel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FieldName = styled.div`
  font-family: Nunito Sans, sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #181b1f;
`;

const FieldHint = styled.div`
  font-family: Nunito Sans, sans-serif;
  font-size: 12px;
  line-height: 1.4;
  color: #899093;
`;

const FieldKey = styled.code`
  font-family: monospace;
  font-size: 11px;
  color: #b0b6b8;
`;

const ControlWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  max-width: 420px;
  width: 100%;
`;

const Select = styled.select`
  height: 44px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-family: Nunito Sans, sans-serif;
  font-size: 14px;
  padding: 0 12px;
  color: #494c5b;
  outline: none;
  background: white;
  width: 100%;
  box-sizing: border-box;
`;

const TextInput = styled.input`
  width: 100%;
  height: 44px;
  padding: 0 12px 0 16px;
  background: #ffffff;
  border: 1px solid rgb(226 232 240);
  border-radius: 4px;
  font-family: Roboto, sans-serif;
  font-size: 14px;
  color: #494c5b;
  outline: none;
  box-sizing: border-box;
  transition: 0.4s;
  :hover,
  :focus {
    border: 1px solid #899093;
  }
`;

const JsonField = styled.textarea`
  width: 100%;
  min-height: 72px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-family: monospace;
  font-size: 12px;
  padding: 10px 12px;
  color: #494c5b;
  outline: none;
  resize: vertical;
  box-sizing: border-box;
  &:focus {
    border-color: #68ad18;
  }
`;

const Unit = styled.span`
  font-family: Nunito Sans, sans-serif;
  font-size: 13px;
  color: #899093;
  flex-shrink: 0;
`;

const SwitchLabel = styled.span`
  font-family: Nunito Sans, sans-serif;
  font-size: 13px;
  color: #494c5b;
`;

const SettingsTextarea = styled.textarea`
  width: 100%;
  min-height: 400px;
  border: 1px solid #ebeeef;
  border-radius: 6px;
  font-family: monospace;
  font-size: 13px;
  padding: 16px;
  color: #494c5b;
  outline: none;
  resize: vertical;
  box-sizing: border-box;
  line-height: 1.5;
  &:focus {
    border-color: #68ad18;
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 16px;
  flex-wrap: wrap;
`;

const ActionButton = styled(Button)`
  width: auto;
  min-width: 100px;
`;

const Message = styled.div<{ error?: boolean }>`
  margin-top: 12px;
  font-family: Nunito Sans, sans-serif;
  font-size: 13px;
  color: ${({ error }) => (error ? "#e74c3c" : "#68ad18")};
`;

const VersionInfo = styled.div`
  font-family: Nunito Sans, sans-serif;
  font-size: 12px;
  color: #899093;
  margin-top: 8px;
`;

const LoadingWrap = styled.div`
  display: flex;
  justify-content: center;
  padding: 48px 0;
`;

// ── Controls ───────────────────────────────────────────────────

/**
 * Object/array values are edited as text so a half-typed document does not
 * collapse the row on every keystroke; the parent only sees parseable values.
 */
const JsonValueField: React.FC<{
  value: unknown;
  onChange: (value: unknown) => void;
}> = ({ value, onChange }) => {
  const [text, setText] = useState(() => JSON.stringify(value ?? null, null, 2));
  const [error, setError] = useState(false);

  // Re-sync when the document is replaced from outside (discard, reload).
  useEffect(() => {
    const next = JSON.stringify(value ?? null, null, 2);
    setText((prev) => {
      try {
        return stableStringify(JSON.parse(prev)) === stableStringify(value) ? prev : next;
      } catch {
        return next;
      }
    });
    setError(false);
  }, [value]);

  return (
    <ControlWrap style={{ flexDirection: "column", alignItems: "stretch", gap: 4 }}>
      <JsonField
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          try {
            onChange(JSON.parse(e.target.value));
            setError(false);
          } catch {
            setError(true);
          }
        }}
      />
      {error && <FieldHint style={{ color: "#e74c3c" }}>Invalid JSON — not saved</FieldHint>}
    </ControlWrap>
  );
};

const FieldControl: React.FC<{
  field: Field;
  value: unknown;
  onChange: (value: unknown) => void;
}> = ({ field, value, onChange }) => {
  if (field.kind === "select" && field.options) {
    const options = field.options;
    // The stored value may not be one of the options (schema changed since it
    // was saved) — keep it visible rather than silently snapping to another.
    const current = options.find((o) => stableStringify(o.value) === stableStringify(value));
    return (
      <ControlWrap>
        <Select
          value={current ? stableStringify(current.value) : "__unknown__"}
          onChange={(e) => {
            const picked = options.find(
              (o) => stableStringify(o.value) === e.target.value
            );
            if (picked) onChange(picked.value);
          }}
        >
          {!current && (
            <option value="__unknown__">
              {value === undefined ? "Not set" : `${String(value)} (not in schema)`}
            </option>
          )}
          {options.map((o) => (
            <option key={stableStringify(o.value)} value={stableStringify(o.value)}>
              {o.label}
              {/* Which option a brand-new user lands on, straight from the schema. */}
              {field.default !== undefined &&
              stableStringify(o.value) === stableStringify(field.default)
                ? " · default for new users"
                : ""}
            </option>
          ))}
        </Select>
      </ControlWrap>
    );
  }

  if (field.kind === "boolean") {
    const on = value === true;
    return (
      <ControlWrap>
        <Switch on={on} onToggle={() => onChange(!on)} ariaLabel={field.label} />
        <SwitchLabel>{on ? "On" : "Off"}</SwitchLabel>
      </ControlWrap>
    );
  }

  if (field.kind === "number") {
    return (
      <ControlWrap>
        <NumericInput
          value={typeof value === "number" ? value : 0}
          onChange={onChange}
          min={field.min}
          max={field.max}
        />
        {field.unit && <Unit>{field.unit}</Unit>}
      </ControlWrap>
    );
  }

  if (field.kind === "text") {
    return (
      <ControlWrap>
        <TextInput
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(e.target.value)}
        />
      </ControlWrap>
    );
  }

  return <JsonValueField value={value} onChange={onChange} />;
};

interface Props {
  userId: string;
}

const UserSettingsSubTab: React.FC<Props> = ({ userId }) => {
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [baseline, setBaseline] = useState<Record<string, unknown>>({});
  const [schemaProps, setSchemaProps] = useState<Record<string, SchemaProp>>({});
  const [schemaVersion, setSchemaVersion] = useState<string | null>(null);
  const [isDefault, setIsDefault] = useState(false);
  const [jsonMode, setJsonMode] = useState(false);
  const [jsonText, setJsonText] = useState("{}");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ text: string; error: boolean } | null>(null);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const [settingsRes, schemaRes] = await Promise.all([
        getUserSettings(userId),
        getSettingsSchema().catch(() => null),
      ]);

      const props: Record<string, SchemaProp> =
        (schemaRes?.schema as { properties?: Record<string, SchemaProp> } | null)
          ?.properties ?? {};
      // A user with nothing stored gets the schema's defaults straight from the
      // API — the same values the backend resolves for them — so they are shown
      // as-is. The default for new users is changed in Admin → Settings schema.
      const stored = (settingsRes.settings as Record<string, unknown> | null) ?? {};
      const storedIsDefault = !!settingsRes.isDefault;

      const initialFields = buildFields(props, stored);
      const withFilled = withDefaults(stored, initialFields);

      setSchemaProps(props);
      setValues(withFilled);
      setBaseline(withFilled);
      setSchemaVersion(settingsRes.schemaVersion ?? schemaRes?.version ?? null);
      setIsDefault(storedIsDefault);
      setJsonText(JSON.stringify(withFilled, null, 2));
    } catch (e) {
      console.error("Failed to fetch user settings:", e);
      setMessage({ text: "Failed to load settings", error: true });
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const fields = useMemo(() => buildFields(schemaProps, values), [schemaProps, values]);
  const visibleFields = useMemo(
    () => fields.filter((f) => !f.visibleWhen || f.visibleWhen(values)),
    [fields, values]
  );

  const dirty = stableStringify(values) !== stableStringify(baseline);

  const setValue = (key: string, value: unknown) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const handleToggleJsonMode = () => {
    setMessage(null);
    if (!jsonMode) {
      setJsonText(JSON.stringify(values, null, 2));
      setJsonMode(true);
      return;
    }
    try {
      const parsed = JSON.parse(jsonText);
      if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
        setMessage({ text: "Settings must be a JSON object", error: true });
        return;
      }
      setValues(parsed as Record<string, unknown>);
      setJsonMode(false);
    } catch {
      setMessage({ text: "Invalid JSON format", error: true });
    }
  };

  const handleSave = async () => {
    setMessage(null);

    let payload = values;
    if (jsonMode) {
      try {
        const parsed = JSON.parse(jsonText);
        if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
          setMessage({ text: "Settings must be a JSON object", error: true });
          return;
        }
        payload = parsed as Record<string, unknown>;
      } catch {
        setMessage({ text: "Invalid JSON format", error: true });
        return;
      }
    }

    setSaving(true);
    try {
      await updateUserSettings(userId, {
        settings: payload,
        schemaVersion: schemaVersion ?? undefined,
      });
      setValues(payload);
      setBaseline(payload);
      setIsDefault(false);
      setMessage({ text: "Settings saved successfully", error: false });
    } catch (e) {
      setMessage({
        text: (e as Error).message || "Failed to save settings",
        error: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    setValues(baseline);
    setJsonText(JSON.stringify(baseline, null, 2));
    setMessage(null);
  };

  if (loading) {
    return (
      <LoadingWrap>
        <Spinner />
      </LoadingWrap>
    );
  }

  return (
    <Container>
      <EditorHeader>
        <Text size={14} type="heading" color="#181B1F">
          User settings
        </Text>
        <ModeButton onClick={handleToggleJsonMode}>
          {jsonMode ? "Back to form" : "Edit as JSON"}
        </ModeButton>
      </EditorHeader>

      {jsonMode ? (
        <SettingsTextarea
          value={jsonText}
          onChange={(e) => setJsonText(e.target.value)}
        />
      ) : (
        <Card>
          {visibleFields.map((field) => (
            <FieldRow key={field.key}>
              <FieldLabel>
                <FieldName>{field.label}</FieldName>
                {field.description && <FieldHint>{field.description}</FieldHint>}
                <FieldKey>{field.key}</FieldKey>
              </FieldLabel>
              <FieldControl
                field={field}
                value={values[field.key]}
                onChange={(v) => setValue(field.key, v)}
              />
            </FieldRow>
          ))}
        </Card>
      )}

      <VersionInfo>
        {schemaVersion ? `Version: ${schemaVersion}` : "No settings schema defined"}
        {isDefault ? " · defaults (nothing saved for this user yet)" : " · custom"}
      </VersionInfo>

      <Actions>
        <ActionButton onClick={handleSave} disabled={saving || (!dirty && !isDefault && !jsonMode)}>
          {saving ? "Saving..." : "Save"}
        </ActionButton>
        <ActionButton kind="outline" onClick={handleDiscard} disabled={!dirty}>
          Discard changes
        </ActionButton>
      </Actions>

      {message && <Message error={message.error}>{message.text}</Message>}
    </Container>
  );
};

export default UserSettingsSubTab;
